import { livescoreClient } from '@/api/livescore/client';
import { ApiFormationsEventIdGet200ResponseDataFormationsAwayInnerInner } from '@/api/livescore/generated';
import LivescorePlayerStats from '@/components/atoms/LivescorePlayerStats/LivescorePlayerStats';
import Substitution from '@/components/icons/Substitution';
import { PREMATCH_STATUSES, useLivescoreMatch } from '@/context/LivescoreMatchContext';
import { useWindowSize } from '@/hooks/useWindowResize';
import { cn } from '@/utils/cn';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import Substitutions from './subcomponents/Substitutions';
import Starters from './subcomponents/Starters';
import Substitutes from './subcomponents/Substitutes';
import SubstitutionsSkeleton from './subcomponents/SubstitutionsSkeleton';
import StartersSkeleton from './subcomponents/StartersSkeleton';
import SubstitutesSkeleton from './subcomponents/SubstitutesSkeleton';
import LivescoreEntityLink from '@/components/atoms/LivescoreEntityLink/LivescoreEntityLink';

const smartWidths = {
  // For 2 lines (e.g., Goalkeeper + the rest)
  '2': [15, 85], // 15% for Goalkeeper, 85% for the rest

  // For 3 lines (e.g., Goalkeeper, Defenders, the rest)
  '3': [15, 40, 45], // 15% GK, 40% DEF, 45% MID+FWD

  // For 4 lines (e.g., Goalkeeper, Defenders, Midfielders, Forwards -> as in 4-3-3)
  '4': [
    15, // Goalkeeper: small zone near the goal
    35, // Defenders: large defensive zone
    30, // Midfielders: midfield zone
    20, // Forwards: attack zone
  ], // Total: 15 + 35 + 30 + 20 = 100%

  // For 5 lines (e.g., Goalkeeper, Defenders, Def. Midfielders, Off. Midfielders, Forwards -> as in 4-2-3-1)
  '5': [
    15, // Goalkeeper
    30, // Defenders
    20, // Defensive Midfielders
    20, // Offensive Midfielders
    15, // Forwards
  ], // Total: 15 + 30 + 20 + 20 + 15 = 100%
};

type PlayerType = ApiFormationsEventIdGet200ResponseDataFormationsAwayInnerInner;

const Player = ({ player, showStats, competitor }: { player: PlayerType; showStats?: boolean; competitor: string }) => {
  const { match } = useLivescoreMatch();
  const { width } = useWindowSize();

  const stats =
    match?.statistics?.totals?.competitors
      ?.find((c) => c.qualifier === competitor)
      ?.players?.find((p) => p.id === player.id)?.statistics ?? {};

  return (
    <LivescoreEntityLink
      href={`/livescore/player/${player.slug ? player.slug : player.id}`}
      className={cn('relative pb-3', competitor === 'away' && player.type === 'goalkeeper' && 'translate-y-4')}
    >
      <div className="relative w-10 h-10">
        <Image
          src={player.img ? player.img : '/images/player.png'}
          alt={player.name}
          layout="fill"
          objectFit="contain"
          className="w-full absolute rounded-t-xl"
        />
      </div>
      <div className="absolute sm:bottom-[1px] bottom-1 left-1/2 -translate-x-1/2 flex items-center flex-col">
        <div className="flex flex-row items-end">
          {!player.starter ? (
            <span className="h-2 w-2.5 sm:h-[10px] sm:w-3.5 rounded-t-[2.5px] dark:bg-white bg-black flex items-center justify-center ml-2">
              <Substitution
                width={width ? (width > 600 ? 9 : 7) : 7}
                height={width ? (width > 600 ? 9 : 7) : 7}
                className="mr-0.5"
              />
            </span>
          ) : null}
          {showStats && (
            <span className={cn('-mb-[2px]', player.starter ? 'pl-8' : 'pl-6')}>
              <LivescorePlayerStats stats={stats} position={player.type ?? ''} />
            </span>
          )}
        </div>
        <p className="text-black dark:text-white sm:text-[9px] text-[8px] dark:bg-gray-650 bg-white_theme-100 w-max px-[7.5px] py-[2.5px] rounded-[5px]">
          {player.jersey_number} <b className="ml-0.5 sm:ml-0">{player?.name?.split(',')[0]}</b>
        </p>
      </div>
    </LivescoreEntityLink>
  );
};

const LivescoreFormationDetails = () => {
  const { match, formation, setFormation, setSubstitutes, setSubstitutions, setStarters } = useLivescoreMatch();
  const [loading, setLoading] = useState(true);
  const { width } = useWindowSize();

  useEffect(() => {
    if (formation) {
      setLoading(false);
      return;
    }

    const getFormation = async () => {
      setLoading(true);
      const formationResponse = await livescoreClient.apiFormationsEventIdGet({ eventId: match.sport_event.id });
      setFormation(formationResponse.data.data.formations);
      setSubstitutes(formationResponse.data.data.substitutes);
      setSubstitutions(formationResponse.data.data.substitutions);
      setStarters(formationResponse.data.data.starters);
      setLoading(false);
    };

    getFormation();
  }, []);

  return (
    <div className="w-full">
      <div className="w-full relative sm:px-8">
        <div className="w-full aspect-[1/1.49647] sm:aspect-[1.49647]">
          <Image
            src="/images/field.svg"
            alt="boisko"
            layout="fill"
            objectFit="contain"
            className={cn(
              loading || (width === undefined && 'animate-pulse'),
              'rotate-90 sm:rotate-0 scale-[1.49647] sm:scale-100',
            )}
          />
        </div>
        <div
          className={cn(
            'absolute inset-0 flex sm:flex-row flex-col',
            loading || width === undefined ? 'sm:px-8' : 'py-4',
          )}
        >
          {formation && width ? (
            <>
              <div className="relative w-full sm:h-full h-1/2 flex sm:flex-row flex-col sm:pr-4 sm:pl-2 py-2 sm:py-0 sm:translate-y-0">
                {formation?.home?.map((value, index) => {
                  return (
                    <div
                      style={{
                        [width > 600 ? 'width' : 'height']: `${
                          formation?.home?.length
                            ? smartWidths[`${formation?.home?.length}` as keyof typeof smartWidths][index]
                            : 100
                        }%`,
                      }}
                      className={cn(
                        'flex sm:flex-col flex-row-reverse justify-center items-center',
                        value.length > 4 ? 'justify-around' : 'justify-center gap-12',
                      )}
                      key={`home ${index}`}
                    >
                      {[...value].reverse().map((player) => {
                        return (
                          <Player
                            showStats={!PREMATCH_STATUSES.includes(match.sport_event_status.status)}
                            key={player.name}
                            player={player}
                            competitor="home"
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <div className="relative w-full sm:h-full h-1/2 flex sm:flex-row flex-col sm:pl-4 sm:pr-3 py-2 sm:py-0">
                {[...(formation?.away ?? [])]?.reverse().map((value, index) => {
                  return (
                    <div
                      style={{
                        [width > 600 ? 'width' : 'height']: `${
                          formation?.away?.length
                            ? smartWidths[`${formation?.away?.length}` as keyof typeof smartWidths][index]
                            : 100
                        }%`,
                      }}
                      className={cn(
                        'w-full flex sm:flex-col flex-row-reverse items-center',
                        value.length > 4 ? 'justify-around' : 'justify-center gap-12',
                        index === (formation?.away?.length ?? 0) - 1 && 'items-end sm:-translate-x-0',
                      )}
                      key={`away ${index}`}
                    >
                      {value.map((player) => (
                        <Player
                          showStats={!PREMATCH_STATUSES.includes(match.sport_event_status.status)}
                          key={player.name}
                          player={player}
                          competitor="away"
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            !loading && (
              <p className="text-xs dark:bg-black-500 bg-white_theme-100 dark:sm:text-gray-400 sm:text-gray-600 uppercase font-medium backdrop-blur-lg w-max px-3 py-2 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                Nie znaleziono formacji dla tego meczu
              </p>
            )
          )}
        </div>
      </div>
      <div className="mt-4">
        {loading ? (
          <>
            <SubstitutionsSkeleton />
            <StartersSkeleton />
            <SubstitutesSkeleton />
          </>
        ) : (
          <>
            <Substitutions />
            <Starters />
            <Substitutes />
          </>
        )}
      </div>
    </div>
  );
};

export default LivescoreFormationDetails;
